/**
 * PSP/CRE Tool v2.0 - National Caveats Configuration
 * 
 * Purpose: Define national caveats (restrictions) that individual nations place
 * on their forces when operating as part of multinational coalitions.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 21, 2024
 * Version: 2.0 - Enhanced with icons.js integration
 * 
 * References:
 * - NATO operational experience
 * - Romanian Army multinational doctrine
 * - Common coalition operational restrictions
 * - Ukraine 2022-2024 multinational support lessons
 * 
 * Caveats affect:
 * - Available missions
 * - Operating environments
 * - Rules of engagement
 * - Medical evacuation priorities
 * 
 * Usage:
 * import { 
 *   CAVEAT_TYPES,
 *   NATIONAL_CAVEAT_PRESETS,
 *   calculateCaveatImpact
 * } from '../constants/nationalCaveats';
 */

import { ICONS, STATUS_ICONS, TACTICAL_ICONS, MEDICAL_ICONS } from '../utils/icons';

// ============================================================================
// CAVEAT CATEGORIES
// ============================================================================

export const CAVEAT_CATEGORIES = {
  GEOGRAPHIC: {
    id: 'GEOGRAPHIC',
    label: 'Geographic/Territorial',
    icon: ICONS.GENERAL.GLOBE,
    description: 'Restrictions on operational areas'
  },
  MISSION: {
    id: 'MISSION',
    label: 'Mission Type',
    icon: TACTICAL_ICONS.TARGET,
    description: 'Restrictions on types of operations'
  },
  COMBAT: {
    id: 'COMBAT',
    label: 'Combat Operations',
    icon: TACTICAL_ICONS.ATTACK,
    description: 'Restrictions on combat activities'
  },
  ENVIRONMENT: {
    id: 'ENVIRONMENT',
    label: 'Environment',
    icon: ICONS.WEATHER.WARNING,
    description: 'Environmental operational restrictions'
  },
  MEDICAL: {
    id: 'MEDICAL',
    label: 'Medical',
    icon: MEDICAL_ICONS.HOSPITAL,
    description: 'Medical evacuation and treatment restrictions'
  },
  ROE: {
    id: 'ROE',
    label: 'Rules of Engagement',
    icon: ICONS.GENERAL.DOCUMENT,
    description: 'ROE restrictions'
  },
  C2: {
    id: 'C2',
    label: 'Command & Control',
    icon: ICONS.GENERAL.RADIO,
    description: 'Command authority restrictions'
  }
};

// ============================================================================
// NATIONAL CAVEAT DEFINITIONS
// ============================================================================

export const CAVEAT_TYPES = {
  // Geographic/Territorial Caveats
  NO_CROSS_BORDER: {
    id: 'NO_CROSS_BORDER',
    label: 'No Cross-Border Operations',
    description: 'Unit cannot operate outside designated theater boundaries',
    impact: 'Limits pursuit operations and deep strikes',
    category: 'GEOGRAPHIC',
    icon: ICONS.STATUS.BLOCKED,
    severity: 'HIGH',
    
    ukraine_relevance: 'Many NATO contributors restricted from operations in Russian territory'
  },
  
  THEATER_ONLY: {
    id: 'THEATER_ONLY',
    label: 'Theater Area Only',
    description: 'Unit restricted to specific geographic area within theater',
    impact: 'May not be available for all missions',
    category: 'GEOGRAPHIC',
    icon: ICONS.GENERAL.PIN,
    severity: 'MEDIUM'
  },
  
  // Mission Type Caveats
  NO_OFFENSIVE: {
    id: 'NO_OFFENSIVE',
    label: 'No Offensive Operations',
    description: 'Unit can only conduct defensive operations',
    impact: 'Cannot participate in attacks, only defense and security',
    category: 'MISSION',
    icon: TACTICAL_ICONS.DEFEND,
    severity: 'HIGH',
    restricted_missions: ['MAIN_EFFORT', 'SUPPORTING'],
    
    ukraine_relevance: 'Some partners restricted to defensive equipment only'
  },
  
  DEFENSE_ONLY: {
    id: 'DEFENSE_ONLY',
    label: 'Defensive Operations Only',
    description: 'Unit limited to static defensive positions',
    impact: 'No movement to contact or exploitation',
    category: 'MISSION',
    icon: TACTICAL_ICONS.DEFEND,
    severity: 'HIGH',
    restricted_postures: ['DELIBERATE_ATTACK', 'HASTY_ATTACK', 'MOVEMENT_TO_CONTACT', 'EXPLOITATION', 'PURSUIT']
  },
  
  SECURITY_ONLY: {
    id: 'SECURITY_ONLY',
    label: 'Security Operations Only',
    description: 'Unit limited to rear area security',
    impact: 'No direct combat operations',
    category: 'MISSION',
    icon: ICONS.GENERAL.LOCK,
    severity: 'MEDIUM',
    forced_operational_state: 'SUPPORT_ACTIVE'
  },
  
  // Combat Caveats
  NO_NIGHT_OPS: {
    id: 'NO_NIGHT_OPS',
    label: 'No Night Operations',
    description: 'Unit cannot conduct operations during darkness',
    impact: 'Reduces operational tempo, predictable activity windows',
    category: 'COMBAT',
    icon: ICONS.WEATHER.MOON,
    severity: 'MEDIUM',
    medical_impact: { evac_delay_hours: 2 },
    
    ukraine_relevance: 'Night operations critical for survival in drone-saturated environment'
  },
  
  NO_URBAN: {
    id: 'NO_URBAN',
    label: 'No Urban Operations',
    description: 'Unit cannot conduct operations in built-up areas',
    impact: 'May bypass cities, limiting operational options',
    category: 'COMBAT',
    icon: ICONS.GENERAL.BUILDING,
    severity: 'MEDIUM',
    
    ukraine_relevance: 'Urban combat (Mariupol, Bakhmut) caused highest casualty rates'
  },
  
  // CBRN Caveats
  NO_CBRN: {
    id: 'NO_CBRN',
    label: 'No CBRN Environment',
    description: 'Unit cannot operate in contaminated environment',
    impact: 'Must withdraw if CBRN threat detected',
    category: 'ENVIRONMENT',
    icon: ICONS.STATUS.HAZARD,
    severity: 'HIGH'
  },
  
  LIMITED_CBRN: {
    id: 'LIMITED_CBRN',
    label: 'Limited CBRN Capability',
    description: 'Unit has basic CBRN protection only',
    impact: 'Can operate in low-level contamination only',
    category: 'ENVIRONMENT',
    icon: ICONS.STATUS.WARNING,
    severity: 'MEDIUM'
  },
  
  // Medical/Casualty Caveats
  NATIONAL_MEDEVAC: {
    id: 'NATIONAL_MEDEVAC',
    label: 'National MEDEVAC Only',
    description: 'Casualties must be evacuated by national assets only',
    impact: 'May delay evacuation if national assets unavailable',
    category: 'MEDICAL',
    icon: MEDICAL_ICONS.AMBULANCE,
    severity: 'MEDIUM',
    medical_impact: { evac_delay_hours: 1, requires_national_assets: true },
    
    ukraine_relevance: 'Common in international volunteer units'
  },
  
  NATIONAL_TREATMENT: {
    id: 'NATIONAL_TREATMENT',
    label: 'National Medical Treatment',
    description: 'Casualties require treatment at national MTFs',
    impact: 'May bypass closer coalition MTFs',
    category: 'MEDICAL',
    icon: MEDICAL_ICONS.HOSPITAL,
    severity: 'MEDIUM',
    medical_impact: { bypass_coalition_mtf: true, increased_evac_distance: true }
  },
  
  IMMEDIATE_REPATRIATION: {
    id: 'IMMEDIATE_REPATRIATION',
    label: 'Immediate Strategic Repatriation',
    description: 'Serious casualties must be immediately evacuated to home country',
    impact: 'Increased strategic airlift requirements',
    category: 'MEDICAL',
    icon: ICONS.TRANSPORT.AIRPLANE,
    severity: 'LOW',
    medical_impact: { strategic_evac_priority: 'HIGH' }
  },
  
  // Rules of Engagement
  RESTRICTIVE_ROE: {
    id: 'RESTRICTIVE_ROE',
    label: 'Restrictive ROE',
    description: 'More restrictive rules of engagement than coalition standard',
    impact: 'May limit response options, increase friendly casualties',
    category: 'ROE',
    icon: ICONS.GENERAL.DOCUMENT,
    severity: 'MEDIUM',
    casualty_modifier: 1.1  // 10% higher casualties
  },
  
  NO_FIRE_SUPPORT: {
    id: 'NO_FIRE_SUPPORT',
    label: 'Limited Fire Support',
    description: 'Cannot call for coalition fire support',
    impact: 'Must rely on organic fires only',
    category: 'ROE',
    icon: TACTICAL_ICONS.ARTILLERY,
    severity: 'HIGH',
    casualty_modifier: 1.15
  },
  
  // Command & Control
  NATIONAL_COMMAND: {
    id: 'NATIONAL_COMMAND',
    label: 'National Command Authority',
    description: 'Requires national approval for certain operations',
    impact: 'Delayed decision-making in time-sensitive situations',
    category: 'C2',
    icon: ICONS.GENERAL.GEAR,
    severity: 'MEDIUM',
    medical_impact: { command_delay_hours: 0.5 }
  },
  
  LIAISON_REQUIRED: {
    id: 'LIAISON_REQUIRED',
    label: 'National Liaison Required',
    description: 'National liaison officer must approve operations',
    impact: 'Additional coordination requirements',
    category: 'C2',
    icon: ICONS.GENERAL.USER,
    severity: 'LOW'
  }
};

// ============================================================================
// NATIONAL CAVEAT PRESETS
// ============================================================================

export const NATIONAL_CAVEAT_PRESETS = {
  NONE: {
    id: 'NONE',
    label: 'No Caveats',
    description: 'Full operational flexibility',
    caveats: [],
    icon: STATUS_ICONS.GREEN,
    status_color: 'success'
  },
  
  STANDARD: {
    id: 'STANDARD',
    label: 'Standard NATO',
    description: 'Standard coalition participation',
    caveats: ['NATIONAL_MEDEVAC'],
    icon: STATUS_ICONS.BLUE,
    status_color: 'info'
  },
  
  DEFENSIVE: {
    id: 'DEFENSIVE',
    label: 'Defensive Focus',
    description: 'Primarily defensive operations',
    caveats: ['DEFENSE_ONLY', 'NATIONAL_MEDEVAC', 'NATIONAL_COMMAND'],
    icon: TACTICAL_ICONS.DEFEND,
    status_color: 'warning'
  },
  
  RESTRICTED: {
    id: 'RESTRICTED',
    label: 'Highly Restricted',
    description: 'Significant operational limitations',
    caveats: ['NO_OFFENSIVE', 'NO_NIGHT_OPS', 'NO_URBAN', 'NATIONAL_MEDEVAC', 'NATIONAL_TREATMENT', 'RESTRICTIVE_ROE'],
    icon: STATUS_ICONS.YELLOW_WARNING,
    status_color: 'warning'
  },
  
  SECURITY: {
    id: 'SECURITY',
    label: 'Security Ops Only',
    description: 'Rear area security only',
    caveats: ['SECURITY_ONLY', 'NO_OFFENSIVE', 'NATIONAL_MEDEVAC'],
    icon: ICONS.GENERAL.LOCK,
    status_color: 'info'
  },
  
  MEDICAL_ONLY: {
    id: 'MEDICAL_ONLY',
    label: 'Medical Support Only',
    description: 'Non-combat medical support',
    caveats: ['SECURITY_ONLY', 'NATIONAL_MEDEVAC', 'IMMEDIATE_REPATRIATION'],
    icon: MEDICAL_ICONS.HOSPITAL,
    status_color: 'info'
  },
  
  TRAINING_ONLY: {
    id: 'TRAINING_ONLY',
    label: 'Training Support Only',
    description: 'Non-combat training mission',
    caveats: ['SECURITY_ONLY', 'NO_OFFENSIVE', 'NO_CROSS_BORDER', 'NATIONAL_MEDEVAC'],
    icon: ICONS.GENERAL.GEAR,
    status_color: 'info',
    
    ukraine_relevance: 'Many NATO trainers operate under this caveat'
  },
  
  CUSTOM: {
    id: 'CUSTOM',
    label: 'Custom',
    description: 'Select individual caveats',
    caveats: [],
    icon: ICONS.GENERAL.GEAR,
    status_color: 'default'
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all caveat types as array for UI
 * @returns {Array} Array of caveat configurations
 */
export function getCaveatTypesArray() {
  return Object.values(CAVEAT_TYPES).map(caveat => ({
    ...caveat,
    category_icon: CAVEAT_CATEGORIES[caveat.category]?.icon
  }));
}

/**
 * Get caveats by category
 * @param {string} category - Category ID
 * @returns {Array} Filtered caveats
 */
export function getCaveatsByCategory(category) {
  return Object.values(CAVEAT_TYPES).filter(c => c.category === category);
}

/**
 * Get all caveat categories
 * @returns {Array} Category IDs
 */
export function getCaveatCategories() {
  return Object.values(CAVEAT_CATEGORIES);
}

/**
 * Get caveat preset options for dropdown
 * @returns {Array} Preset options
 */
export function getCaveatPresetOptions() {
  return Object.entries(NATIONAL_CAVEAT_PRESETS).map(([key, preset]) => ({
    value: key,
    label: preset.label,
    icon: preset.icon,
    description: preset.description,
    caveat_count: preset.caveats.length
  }));
}

/**
 * Get caveats from preset
 * @param {string} presetKey - Preset ID
 * @returns {Array} Caveat configurations
 */
export function getCaveatsFromPreset(presetKey) {
  const preset = NATIONAL_CAVEAT_PRESETS[presetKey];
  if (!preset) return [];
  return preset.caveats.map(id => CAVEAT_TYPES[id]).filter(Boolean);
}

/**
 * Calculate combined caveat impact
 * @param {Array} caveatIds - Array of caveat IDs
 * @returns {Object} Aggregated impact
 */
export function calculateCaveatImpact(caveatIds) {
  const impact = {
    casualty_modifier: 1.0,
    evac_delay_hours: 0,
    command_delay_hours: 0,
    restricted_missions: [],
    restricted_postures: [],
    forced_operational_state: null,
    requires_national_assets: false,
    bypass_coalition_mtf: false,
    severity: 'NONE',
    icon: STATUS_ICONS.GREEN
  };
  
  if (!caveatIds || caveatIds.length === 0) {
    return impact;
  }
  
  let maxSeverity = 0;
  const severityMap = { 'LOW': 1, 'MEDIUM': 2, 'HIGH': 3 };
  
  caveatIds.forEach(id => {
    const caveat = CAVEAT_TYPES[id];
    if (!caveat) return;
    
    // Track max severity
    const caveatSeverity = severityMap[caveat.severity] || 0;
    if (caveatSeverity > maxSeverity) {
      maxSeverity = caveatSeverity;
    }
    
    // Accumulate casualty modifier
    if (caveat.casualty_modifier) {
      impact.casualty_modifier *= caveat.casualty_modifier;
    }
    
    // Accumulate medical impacts
    if (caveat.medical_impact) {
      if (caveat.medical_impact.evac_delay_hours) {
        impact.evac_delay_hours += caveat.medical_impact.evac_delay_hours;
      }
      if (caveat.medical_impact.command_delay_hours) {
        impact.command_delay_hours += caveat.medical_impact.command_delay_hours;
      }
      if (caveat.medical_impact.requires_national_assets) {
        impact.requires_national_assets = true;
      }
      if (caveat.medical_impact.bypass_coalition_mtf) {
        impact.bypass_coalition_mtf = true;
      }
    }
    
    // Collect restrictions
    if (caveat.restricted_missions) {
      impact.restricted_missions.push(...caveat.restricted_missions);
    }
    if (caveat.restricted_postures) {
      impact.restricted_postures.push(...caveat.restricted_postures);
    }
    if (caveat.forced_operational_state) {
      impact.forced_operational_state = caveat.forced_operational_state;
    }
  });
  
  // Remove duplicates
  impact.restricted_missions = [...new Set(impact.restricted_missions)];
  impact.restricted_postures = [...new Set(impact.restricted_postures)];
  
  // Set overall severity
  if (maxSeverity >= 3) {
    impact.severity = 'HIGH';
    impact.icon = STATUS_ICONS.RED;
  } else if (maxSeverity >= 2) {
    impact.severity = 'MEDIUM';
    impact.icon = STATUS_ICONS.YELLOW;
  } else if (maxSeverity >= 1) {
    impact.severity = 'LOW';
    impact.icon = STATUS_ICONS.GREEN;
  }
  
  return impact;
}

/**
 * Check if mission is allowed given caveats
 * @param {string} mission - Mission type
 * @param {string} posture - Operational posture
 * @param {Array} caveatIds - Active caveat IDs
 * @returns {Object} Allowance result
 */
export function isMissionAllowed(mission, posture, caveatIds) {
  const impact = calculateCaveatImpact(caveatIds);
  
  // Check if posture is restricted
  if (impact.restricted_postures.includes(posture)) {
    return {
      allowed: false,
      reason: `Posture "${posture}" restricted by caveats`,
      icon: STATUS_ICONS.RED
    };
  }
  
  // Check if mission is restricted (in context of offensive postures)
  const offensivePostures = ['DELIBERATE_ATTACK', 'HASTY_ATTACK', 'MOVEMENT_TO_CONTACT', 'EXPLOITATION', 'PURSUIT'];
  if (offensivePostures.includes(posture) && impact.restricted_missions.includes(mission)) {
    return {
      allowed: false,
      reason: `Mission "${mission}" restricted in offensive posture`,
      icon: STATUS_ICONS.RED
    };
  }
  
  return {
    allowed: true,
    icon: STATUS_ICONS.GREEN
  };
}

/**
 * Format caveat summary for display
 * @param {Array} caveatIds - Active caveat IDs
 * @returns {string} Summary text
 */
export function formatCaveatSummary(caveatIds) {
  if (!caveatIds || caveatIds.length === 0) {
    return 'No caveats - Full operational flexibility';
  }
  
  const impact = calculateCaveatImpact(caveatIds);
  const parts = [];
  
  if (impact.casualty_modifier > 1.0) {
    parts.push(`+${((impact.casualty_modifier - 1) * 100).toFixed(0)}% casualty risk`);
  }
  if (impact.evac_delay_hours > 0) {
    parts.push(`+${impact.evac_delay_hours}h evac delay`);
  }
  if (impact.restricted_postures.length > 0) {
    parts.push(`${impact.restricted_postures.length} restricted postures`);
  }
  if (impact.requires_national_assets) {
    parts.push('National MEDEVAC required');
  }
  
  return parts.length > 0 ? parts.join(', ') : 'Minor restrictions';
}

/**
 * Validate caveat configuration
 * @param {Array} caveatIds - Caveat IDs
 * @returns {Object} Validation result
 */
export function validateCaveatConfig(caveatIds) {
  const errors = [];
  const warnings = [];
  
  caveatIds.forEach(id => {
    if (!CAVEAT_TYPES[id]) {
      errors.push(`Invalid caveat ID: ${id}`);
    }
  });
  
  // Check for conflicting caveats
  if (caveatIds.includes('NO_OFFENSIVE') && caveatIds.includes('SECURITY_ONLY')) {
    // Not conflicting, just redundant
  }
  
  const impact = calculateCaveatImpact(caveatIds);
  
  if (impact.casualty_modifier > 1.2) {
    warnings.push('Combined caveats significantly increase casualty risk (+20%)');
  }
  
  if (impact.evac_delay_hours > 3) {
    warnings.push('Combined evacuation delays exceed 3 hours - golden hour at risk');
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? STATUS_ICONS.RED : 
          warnings.length > 0 ? STATUS_ICONS.YELLOW : 
          STATUS_ICONS.GREEN
  };
}

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const CAVEAT_EDUCATIONAL = {
  key_principles: [
    'Caveats are restrictions nations place on their forces in coalitions',
    'Medical caveats affect evacuation timing and MTF selection',
    'ROE caveats can increase casualty rates (delayed response)',
    'Understanding caveats essential for multinational medical planning'
  ],
  
  ukraine_lessons: [
    'Many international volunteers operated under strict caveats',
    'National MEDEVAC requirements complicated coalition medical support',
    'Training-only caveats common for NATO advisors',
    'Cross-border restrictions limited some equipment transfers'
  ],
  
  planning_factors: [
    'NATIONAL_MEDEVAC: +1 hour average evacuation delay',
    'RESTRICTIVE_ROE: +10% casualty increase',
    'NO_FIRE_SUPPORT: +15% casualty increase',
    'Combined caveats effects multiply'
  ]
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const nationalCaveatsConstants = {
  CAVEAT_CATEGORIES,
  CAVEAT_TYPES,
  NATIONAL_CAVEAT_PRESETS,
  getCaveatTypesArray,
  getCaveatsByCategory,
  getCaveatCategories,
  getCaveatPresetOptions,
  getCaveatsFromPreset,
  calculateCaveatImpact,
  isMissionAllowed,
  formatCaveatSummary,
  validateCaveatConfig,
  CAVEAT_EDUCATIONAL
};

export default nationalCaveatsConstants;
